import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Storage, translate, Translate } from 'react-jhipster';
import { Button, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IRebateParty } from 'app/shared/model/rebate-party.model';
import { IRebateSetting } from 'app/shared/model/rebate-setting.model';
import { IRebateFee, IRebateFeeUseDealCount } from 'app/shared/model/rebate-fee.model';
import { IRebateShare } from 'app/shared/model/rebate-share.model';
import { IRebateTier } from 'app/shared/model/rebate-tier.model';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

type FeeType = 'Lump Sum' | 'Percentage';
type Operator = '<=' | '>=' | '<' | '>' | '=';

// Bootstrap's own default .form-control height (px) — used by every reactstrap <Input>/ValidatedField
// on this site (the Transaction Fee currency dropdown above, and every field on the "create a new
// bank" screen), so every MUI input here is forced to match it exactly. `!important` is needed
// because MUI's own size="small" styles set the same properties at equal CSS specificity, and
// without it those rules — not ours — win the tie, which is why plain overrides here had no visible
// effect.
const FORM_CONTROL_HEIGHT = 38;

// For bare MUI <Select> root elements, whose own root IS the bordered input box. The top/bottom
// padding on the inner select/input is kept equal on purpose — forcing height to 100% with zero
// padding (an earlier version of this) let the text's own line-height sit off-center within the
// box, so top and bottom looked unbalanced; equal explicit padding can't do that by construction.
// The notched-outline fieldset (the actual visible border) defaults to `top: -5px` — space MUI
// normally reserves for a floating label notch — so the visible border was always ~5px taller than
// whatever height we set on the root itself, regardless of any padding fix. Pinning it to `top: 0`
// is what actually makes the visible box match the currency dropdown's height.
const compactControlSx = {
  height: `${FORM_CONTROL_HEIGHT}px !important`,
  boxSizing: 'border-box',
  '& fieldset': { top: '0px !important' },
  '& .MuiSelect-select': {
    height: 'auto !important',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    paddingTop: '8px !important',
    paddingBottom: '8px !important',
  },
} as const;
// For <TextField>, whose sx root is the outer FormControl wrapper — the bordered input box is
// nested one level down.
// Keeps every party row's name (its chip, or the add row's name field) the same width, so the
// account number fields line up in one column.
const partyNameColumnSx = { width: 200, flexShrink: 0 };

const compactFieldSx = {
  '& .MuiInputBase-root': { height: `${FORM_CONTROL_HEIGHT}px !important`, boxSizing: 'border-box' },
  '& fieldset': { top: '0px !important' },
  '& .MuiInputBase-input': {
    height: 'auto !important',
    boxSizing: 'border-box',
    paddingTop: '8px !important',
    paddingBottom: '8px !important',
  },
} as const;

// This codebase doesn't set MUI's theme direction to 'rtl' (see the manual textAlign flip in
// bank-update.tsx's StyledTextField) — so physical left/right styles on MUI components never
// auto-mirror for Arabic and have to be flipped by hand like this.
function isRtlLocale(): boolean {
  return Storage.session.get('locale') === 'ar-ly';
}

const OPERATORS: Operator[] = ['<=', '>=', '<', '>', '='];

// Short symbol shown both on the closed select and in its own menu items — deal-count operators are
// shown as icons only (no wording), so there's nothing else to render here.
const OPERATOR_SYMBOLS: Record<Operator, string> = {
  '<=': '≤',
  '>=': '≥',
  '<': '<',
  '>': '>',
  '=': '=',
};

interface Party {
  id: string;
  name: string;
  accountNumber?: string;
}

// Al Shmokh's and the bank's own seeded party (RebatePartyService.seedDefaultPartiesForSetting) get
// their account number from Comex Settings / Edit Bank Details instead — only a real third party gets
// the editable Account Number field (ALSHMOKH-66).
const DEFAULT_PARTY_NAME_ALSHMOKH = 'Alshmokh';
const isDefaultParty = (party: Party, bankName?: string) =>
  party.name?.toLowerCase() === DEFAULT_PARTY_NAME_ALSHMOKH.toLowerCase() || (!!bankName && party.name?.toLowerCase() === bankName.toLowerCase());

// Used only by bank-detail.tsx, to key a Percentage-type Transaction Fee range's own amount (which
// has no real currencyId in comex_bank_fee) the same way as every other range.
export const PERCENT_KEY = '__percentage__';

interface ShareRow {
  partyId: string;
  active: boolean;
  valuesByCurrency: Record<string, number>;
}

interface TierDef {
  operator: Operator;
  dealCount: number;
}

// One deal-count bracket within a bucket (e.g. "<= 150 deals" for this range). Scoped to this
// bucket alone — a different range can have a completely different set of brackets. Fee type and
// per-party values are fully independent per currency here too, same as the bucket's own plain
// (non-deal-count) row.
interface BucketTier extends TierDef {
  typesByCurrency?: Record<string, FeeType>;
  shares: ShareRow[];
  sharesHydrated?: boolean;
}

interface FeeBucket {
  id: string;
  sourceId?: string | number;
  from?: number;
  to?: number;
  // Fee type is fully independent per currency: each currency has its own entry here (Lump Sum or
  // Percentage), untouched by any other currency's choice or value. A currency with no entry hasn't
  // had a fee type chosen yet and shows a blank ("Choose one") badge pill (see resolveBucketType).
  typesByCurrency?: Record<string, FeeType>;
  // Whether this bucket's parties' persisted share values have already been applied once — prevents
  // a later reload of sharesByPartyFromToCurrency from clobbering a user's in-progress edit.
  sharesHydrated?: boolean;
  transactionFeeType?: FeeType;
  // The Transaction Fee's own amount(s) for this range, keyed by currencyId (or PERCENT_KEY for a
  // Percentage range) — undefined for the flat/commission bucket, which has no matching range.
  amountsByCurrency?: Record<string, number>;
  shares: ShareRow[];
  tiers: BucketTier[];
}

// The fee type chosen for a specific currency, or undefined if nothing's been chosen yet for it.
function resolveType(typesByCurrency: Record<string, FeeType> | undefined, currencyId: string): FeeType | undefined {
  return typesByCurrency?.[currencyId];
}

function resolveBucketType(bucket: FeeBucket, currencyId: string): FeeType | undefined {
  return resolveType(bucket.typesByCurrency, currencyId);
}

export interface RebateFeeRange {
  id: string | number;
  from: number;
  to: number;
  feeType?: string;
  amountsByCurrency?: Record<string, number>;
}

export interface RebateFeeCurrency {
  id: string | number;
  name: string;
}

const genId = () => Math.random().toString(36).slice(2, 10);

// A range's own upper bound can numerically coincide with the commission row's own from_to (both
// are just "some comex_bank_fee.from_to value"), so every lookup keyed by fromTo must also carry
// rangeType to tell the two apart — this is the composite key used throughout.
function rangeKey(fromTo: number, rangeType: 'commission' | 'range'): string {
  return `${fromTo}|${rangeType}`;
}

// Identifies one conceptual tier within a bucket by its boundary — each party has its own separate
// rebate_tier row (same backend shape as rebate_fee itself), so the boundary is the only thing every
// party's row for "the same" tier has in common.
function tierSlot(operator: Operator, dealCount: number): string {
  return `${operator}|${dealCount}`;
}

function getValue(row: ShareRow | undefined, key: string): number {
  return row?.valuesByCurrency[key] ?? 0;
}

// Distinguishes "no rebate_share row exists yet for this currency" (blank) from "a value of 0 was
// actually saved" — shares are per-currency, so switching to a currency nothing's been entered for
// yet should look like the section did before any share was ever set, not show "0".
function hasValue(row: ShareRow | undefined, key: string): boolean {
  return row !== undefined && Object.prototype.hasOwnProperty.call(row.valuesByCurrency, key);
}

function setRowValue(shares: ShareRow[], partyId: string, key: string, value: number): ShareRow[] {
  return shares.map(s => (s.partyId === partyId ? { ...s, valuesByCurrency: { ...s.valuesByCurrency, [key]: value } } : s));
}

function makeShares(parties: Party[]): ShareRow[] {
  return parties.map(p => ({ partyId: p.id, active: true, valuesByCurrency: {} }));
}

// Reconcile a bucket's share rows with the current party list: keep rows for parties that
// still exist, add empty rows for new parties, drop rows for removed ones.
function reconcileShares(shares: ShareRow[], parties: Party[]): ShareRow[] {
  const partyIds = new Set(parties.map(p => p.id));
  const existingIds = new Set(shares.map(s => s.partyId));
  return [
    ...shares.filter(s => partyIds.has(s.partyId)),
    ...parties.filter(p => !existingIds.has(p.id)).map(p => ({ partyId: p.id, active: true, valuesByCurrency: {} })),
  ];
}

function reconcileBucketParties(bucket: FeeBucket, parties: Party[]): FeeBucket {
  return {
    ...bucket,
    shares: reconcileShares(bucket.shares, parties),
    tiers: bucket.tiers.map(t => ({ ...t, shares: reconcileShares(t.shares, parties) })),
  };
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'rgba(255,255,255,0)',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

// A big, self-explanatory card for one of the two mutually-exclusive rebate split bases — both
// options are shown side by side (rather than hidden behind a plain on/off switch whose meaning
// isn't obvious), with the active one clearly marked by a solid fill instead of just an outline.
function BasisOptionCard({
  selected,
  disabled,
  title,
  onSelect,
}: {
  selected: boolean;
  disabled?: boolean;
  title: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Box
      onClick={() => !disabled && onSelect()}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: selected ? 'primary.main' : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" color={selected ? 'common.white' : 'text.primary'}>
        {title}
      </Typography>
      {selected && <CheckCircleIcon fontSize="small" sx={{ color: 'common.white' }} />}
    </Box>
  );
}

function typeChipSx(type: FeeType) {
  return type === 'Lump Sum'
    ? { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }
    : { backgroundColor: '#f3e5f5', color: '#8e24aa', fontWeight: 600 };
}

function unchosenChipSx() {
  return { backgroundColor: '#eeeeee', color: '#616161', fontWeight: 600 };
}

// The stored value ("Lump Sum" / "Percentage") is always English — it's the API's own data value —
// but the label shown to the user follows the current locale.
function feeTypeLabel(type: FeeType): string {
  return translate(type === 'Lump Sum' ? 'comexApp.bank.details.lumpSum' : 'comexApp.bank.details.percentage');
}

// A Select styled to look like the type Chip it replaces, so the fee type can be changed in place.
// Starts blank ("Choose one") — nothing is persisted until the user actually picks a value; once
// chosen it can still be switched between the two (but never back to blank). When the matching
// Transaction Fee range is Percentage-only, Lump Sum is disabled — a rebate can't be lump-sum on a
// fee that never yields a flat amount.
function FeeTypeBadgeSelect({
  type,
  disableLumpSum,
  disabled,
  onChange,
}: {
  type: FeeType | '';
  disableLumpSum: boolean;
  disabled?: boolean;
  onChange: (type: FeeType) => void;
}) {
  return (
    <Select
      size="small"
      value={type}
      displayEmpty
      disabled={disabled}
      onChange={e => {
        const next = e.target.value as FeeType | '';
        if (next) {
          onChange(next);
        }
      }}
      renderValue={selected => (selected ? feeTypeLabel(selected) : translate('comexApp.bank.details.rebateFee.chooseOne'))}
      sx={{
        ...(type ? typeChipSx(type) : unchosenChipSx()),
        borderRadius: '16px',
        '& .MuiSelect-select': {
          py: 0.25,
          // MUI's own outlined Select reserves space on whichever side the icon renders, via a
          // triple-class `&&&` rule specifically meant to out-rank consumer overrides — `!important`
          // is required to beat it. The padding has to swap sides with the icon itself (flipped for
          // Arabic below): the icon's side needs the larger clearance (bumped past MUI's own 24px
          // default for a clearer gap from the text), while the other side only needs the plain 12px
          // text inset — getting this backwards is what shrank the English side's icon clearance and
          // caused its own overlap.
          paddingLeft: isRtlLocale() ? '34px !important' : '12px !important',
          paddingRight: isRtlLocale() ? '12px !important' : '34px !important',
          fontSize: '0.8125rem',
          fontWeight: 600,
        },
        '& fieldset': { border: 'none' },
        ...(isRtlLocale() ? { '& .MuiSelect-icon': { right: 'auto !important', left: '7px !important' } } : {}),
      }}
    >
      <MenuItem value="Lump Sum" disabled={disableLumpSum}>
        {feeTypeLabel('Lump Sum')}
      </MenuItem>
      <MenuItem value="Percentage">{feeTypeLabel('Percentage')}</MenuItem>
    </Select>
  );
}

function SimpleShareTable({
  bucket,
  type,
  parties,
  currencyKey,
  currencyLabel,
  onChangeValue,
}: {
  bucket: FeeBucket;
  type: FeeType;
  parties: Party[];
  currencyKey: string;
  currencyLabel: string;
  onChangeValue: (partyId: string, value: number) => void;
}) {
  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);

  return (
    <TableContainer>
      <Table size="small" className="fee-table">
        <TableHead className="enhanced-table-bank">
          <TableRow>
            <StyledTableCell>
              <Translate contentKey="comexApp.bank.details.rebateFee.party" />
            </StyledTableCell>
            <StyledTableCell>
              {translate('comexApp.bank.details.rebateFee.share')} {type === 'Lump Sum' ? `(${currencyLabel})` : ''}
            </StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {parties.map(party => {
            const row = bucket.shares.find(s => s.partyId === party.id);
            if (!row) {
              return null;
            }
            const value = getValue(row, currencyKey);
            const valueSet = hasValue(row, currencyKey);
            const isEditing = editingPartyId === party.id;
            return (
              <TableRow key={party.id}>
                <TableCell>{party.name}</TableCell>
                <TableCell>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      autoFocus
                      placeholder={translate('comexApp.bank.details.rebateFee.notSet')}
                      defaultValue={valueSet ? value : ''}
                      onBlur={e => {
                        onChangeValue(party.id, Number(e.target.value) || 0);
                        setEditingPartyId(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      sx={{ width: 110, ...compactFieldSx }}
                    />
                  ) : valueSet ? (
                    <span>
                      {value}
                      {type === 'Percentage' ? '%' : ` ${currencyLabel}`}
                    </span>
                  ) : (
                    <Typography component="span" variant="body2" color="text.secondary">
                      <Translate contentKey="comexApp.bank.details.rebateFee.notSet" />
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditingPartyId(isEditing ? null : party.id)}>
                    {isEditing ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DealCountTable({
  bucket,
  parties,
  currencyId,
  currencyLabel,
  onAddTier,
  onUpdateTierDef,
  onDeleteTierDef,
  onChangeTierType,
  onSaveTierShare,
}: {
  bucket: FeeBucket;
  parties: Party[];
  currencyId: string;
  currencyLabel: string;
  onAddTier: () => void;
  onUpdateTierDef: (oldDef: TierDef, newDef: TierDef) => void;
  onDeleteTierDef: (def: TierDef) => void;
  onChangeTierType: (tier: BucketTier, newType: FeeType) => void;
  onSaveTierShare: (tier: BucketTier, partyId: string, value: number) => void;
}) {
  const [editingTierSlot, setEditingTierSlot] = useState<string | null>(null);

  return (
    <TableContainer>
      <Table size="small" className="fee-table">
        <TableHead className="enhanced-table-bank">
          <TableRow>
            <StyledTableCell>
              <Translate contentKey="comexApp.bank.details.rebateFee.dealCount" />
            </StyledTableCell>
            <StyledTableCell>
              <Translate contentKey="comexApp.bank.details.rebateFee.type" />
            </StyledTableCell>
            {parties.map(p => (
              <StyledTableCell key={p.id}>{p.name}</StyledTableCell>
            ))}
            <StyledTableCell>
              <Translate contentKey="comexApp.bank.details.rebateFee.total" />
            </StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {bucket.tiers.length === 0 && (
            <TableRow>
              <TableCell colSpan={parties.length + 4} align="center">
                <Typography variant="body2" color="text.secondary">
                  <Translate contentKey="comexApp.bank.details.rebateFee.noTiersYet" />
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {bucket.tiers.map(tier => {
            const slot = tierSlot(tier.operator, tier.dealCount);
            const type = resolveType(tier.typesByCurrency, currencyId);
            const synced = type !== undefined;
            const isEditing = editingTierSlot === slot;
            const total = tier.shares.reduce((sum, r) => sum + (r.active ? getValue(r, currencyId) : 0), 0);
            // Follows the same real Transaction Fee amount as the bucket's own (non-deal-count)
            // target — never manually entered.
            const target = bucket.amountsByCurrency?.[currencyId] ?? 0;
            const isBalanced = type === 'Percentage' ? total === 100 : total === target;
            return (
              <TableRow key={slot}>
                <TableCell>
                  {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Select
                        size="small"
                        value={tier.operator}
                        onChange={e => onUpdateTierDef(tier, { ...tier, operator: e.target.value as Operator })}
                        renderValue={selected => OPERATOR_SYMBOLS[selected]}
                        sx={{ minWidth: 60, ...compactControlSx }}
                      >
                        {OPERATORS.map(op => (
                          <MenuItem key={op} value={op}>
                            {OPERATOR_SYMBOLS[op]}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField
                        size="small"
                        type="number"
                        defaultValue={tier.dealCount}
                        onBlur={e => onUpdateTierDef(tier, { ...tier, dealCount: Number(e.target.value) || 0 })}
                        sx={{ width: 80, ...compactFieldSx }}
                      />
                    </Box>
                  ) : (
                    <Chip size="small" label={`${OPERATOR_SYMBOLS[tier.operator]} ${tier.dealCount}`} />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <FeeTypeBadgeSelect
                      type={synced ? type : ''}
                      disableLumpSum={bucket.transactionFeeType === 'Percentage'}
                      onChange={newType => onChangeTierType(tier, newType)}
                    />
                    {synced && type === 'Lump Sum' && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={translate('comexApp.bank.details.rebateFee.target', { amount: target, currency: currencyLabel })}
                      />
                    )}
                  </Box>
                </TableCell>
                {parties.map(party => {
                  const row = tier.shares.find(s => s.partyId === party.id);
                  const value = getValue(row, currencyId);
                  const valueSet = hasValue(row, currencyId);
                  return (
                    <TableCell key={party.id}>
                      {!synced ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          <Translate contentKey="comexApp.bank.details.rebateFee.notSet" />
                        </Typography>
                      ) : isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          placeholder={translate('comexApp.bank.details.rebateFee.notSet')}
                          defaultValue={valueSet ? value : ''}
                          onBlur={e => onSaveTierShare(tier, party.id, Number(e.target.value) || 0)}
                          sx={{ width: 80, ...compactFieldSx }}
                        />
                      ) : valueSet ? (
                        <span>
                          {value}
                          {type === 'Percentage' ? '%' : ` ${currencyLabel}`}
                        </span>
                      ) : (
                        <Typography component="span" variant="body2" color="text.secondary">
                          <Translate contentKey="comexApp.bank.details.rebateFee.notSet" />
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell>
                  {synced && (
                    <Chip
                      size="small"
                      icon={isBalanced ? <CheckCircleIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
                      label={type === 'Percentage' ? `${total}%` : `${total} ${currencyLabel}`}
                      color={isBalanced ? 'success' : 'warning'}
                      variant="outlined"
                      sx={isRtlLocale() ? { '& .MuiChip-icon': { marginLeft: '-4px', marginRight: '4px' } } : undefined}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditingTierSlot(isEditing ? null : slot)}>
                    {isEditing ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                  <Tooltip title={translate('comexApp.bank.details.rebateFee.removeTier')}>
                    <IconButton size="small" color="error" onClick={() => onDeleteTierDef(tier)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button color="primary" onClick={onAddTier}>
          <FontAwesomeIcon icon="plus" /> <Translate contentKey="comexApp.bank.details.rebateFee.addTier" />
        </Button>
      </Box>
    </TableContainer>
  );
}

function FeeBucketCard({
  bucket,
  parties,
  useDealCount,
  isRangeMode,
  currencyId,
  currencyLabel,
  onUpdate,
  onChangeType,
  onSaveShare,
  onAddTier,
  onUpdateTierDef,
  onDeleteTierDef,
  onChangeTierType,
  onSaveTierShare,
}: {
  bucket: FeeBucket;
  parties: Party[];
  useDealCount: boolean;
  isRangeMode: boolean;
  currencyId: string;
  currencyLabel: string;
  onUpdate: (fn: (b: FeeBucket) => FeeBucket) => void;
  onChangeType: (type: FeeType) => void;
  onSaveShare: (partyId: string, value: number) => void;
  onAddTier: () => void;
  onUpdateTierDef: (oldDef: TierDef, newDef: TierDef) => void;
  onDeleteTierDef: (def: TierDef) => void;
  onChangeTierType: (tier: BucketTier, newType: FeeType) => void;
  onSaveTierShare: (tier: BucketTier, partyId: string, value: number) => void;
}) {
  // Fee type is resolved per currency: this currency's own row if one's been chosen, else undefined
  // (blank badge pill, share table hidden). Only meaningful outside deal-count mode — once deal
  // count is on, each tier has its own independent type instead of one shared per bucket.
  const resolvedType = resolveBucketType(bucket, currencyId);
  const synced = resolvedType !== undefined;
  const type: FeeType = resolvedType ?? 'Percentage';
  // Every share — Lump Sum or Percentage — is keyed by the real currency, so switching currencies
  // never mixes values together.
  const bucketKey = currencyId;
  // The target always follows the actual Transaction Fee amount for this currency (a range's own
  // amount, or the bank's commission amount for the flat bucket) — it isn't editable here.
  const targetTotal = bucket.amountsByCurrency?.[bucketKey] ?? 0;
  const total = useMemo(
    () => bucket.shares.reduce((sum, s) => sum + (s.active ? getValue(s, bucketKey) : 0), 0),
    [bucket.shares, bucketKey]
  );
  const isBalanced = type === 'Percentage' ? total === 100 : total === targetTotal;

  const setShareValue = (partyId: string, value: number) => {
    onUpdate(b => ({ ...b, shares: setRowValue(b.shares, partyId, bucketKey, value) }));
    onSaveShare(partyId, value);
  };

  const updateTierLocal = (def: TierDef, fn: (t: BucketTier) => BucketTier) =>
    onUpdate(b => ({
      ...b,
      tiers: b.tiers.map(t => (t.operator === def.operator && t.dealCount === def.dealCount ? fn(t) : t)),
    }));

  const setTierShareValue = (tier: BucketTier, partyId: string, value: number) => {
    updateTierLocal(tier, t => ({ ...t, shares: setRowValue(t.shares, partyId, currencyId, value) }));
    onSaveTierShare(tier, partyId, value);
  };

  return (
    <Paper className="paper-table my-4" sx={{ p: 2, mb: 3 }}>
      <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Grid item>
          {isRangeMode ? (
            <Tooltip title={translate('comexApp.bank.details.rebateFee.editRangeTooltip')}>
              <Typography variant="subtitle1" fontWeight="bold">
                {(bucket.from ?? 0).toLocaleString()} – {(bucket.to ?? 0).toLocaleString()}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="subtitle1" fontWeight="bold">
              <Translate contentKey="comexApp.bank.details.rebateFee.rebateDistribution" />
            </Typography>
          )}
        </Grid>
        {!useDealCount && (
          <Grid item>
            <FeeTypeBadgeSelect
              type={synced ? type : ''}
              disableLumpSum={isRangeMode && bucket.transactionFeeType === 'Percentage'}
              onChange={onChangeType}
            />
          </Grid>
        )}
        {!useDealCount && synced && type === 'Lump Sum' && (
          <Grid item>
            <Tooltip
              title={translate(
                isRangeMode
                  ? 'comexApp.bank.details.rebateFee.followsRangeAmount'
                  : 'comexApp.bank.details.rebateFee.followsCommissionAmount'
              )}
            >
              <Chip
                size="small"
                variant="outlined"
                label={translate('comexApp.bank.details.rebateFee.target', { amount: targetTotal, currency: currencyLabel })}
              />
            </Tooltip>
          </Grid>
        )}
        <Grid item xs />
        {!useDealCount && synced && (
          <Grid item>
            <Chip
              size="small"
              icon={isBalanced ? <CheckCircleIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
              label={
                type === 'Percentage'
                  ? translate('comexApp.bank.details.rebateFee.totalPercentage', { total })
                  : translate('comexApp.bank.details.rebateFee.totalLumpSum', { total, target: targetTotal, currency: currencyLabel })
              }
              color={isBalanced ? 'success' : 'warning'}
              variant="outlined"
              sx={isRtlLocale() ? { '& .MuiChip-icon': { marginLeft: '-4px', marginRight: '4px' } } : undefined}
            />
          </Grid>
        )}
      </Grid>

      {useDealCount ? (
        <DealCountTable
          bucket={bucket}
          parties={parties}
          currencyId={currencyId}
          currencyLabel={currencyLabel}
          onAddTier={onAddTier}
          onUpdateTierDef={onUpdateTierDef}
          onDeleteTierDef={onDeleteTierDef}
          onChangeTierType={onChangeTierType}
          onSaveTierShare={setTierShareValue}
        />
      ) : !synced ? (
        <Typography variant="body2" color="text.secondary">
          <Translate contentKey="comexApp.bank.details.rebateFee.chooseTypeMessage" />
        </Typography>
      ) : (
        <>
          <SimpleShareTable
            bucket={bucket}
            type={type}
            parties={parties}
            currencyKey={bucketKey}
            currencyLabel={currencyLabel}
            onChangeValue={setShareValue}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            <Translate
              contentKey={
                type === 'Percentage'
                  ? 'comexApp.bank.details.rebateFee.percentageHint'
                  : 'comexApp.bank.details.rebateFee.lumpSumHint'
              }
            />
          </Typography>
        </>
      )}
    </Paper>
  );
}

export function RebateFeeSetting({
  bankId,
  bankName,
  commodityType,
  useCommission,
  feeRanges,
  commissionFromTo,
  commissionAmountsByCurrency,
  transactionCurrencyId,
  availableCurrencies,
}: {
  bankId: number;
  // Used only to tell the bank's own seeded rebate party apart from a real third party, so the
  // Account Number field only shows up for the latter (ALSHMOKH-66).
  bankName?: string;
  commodityType: 'International' | 'Local';
  useCommission: boolean;
  feeRanges: RebateFeeRange[];
  // The real comex_bank_fee.from_to of the bank's commission row (fee_type = 'commission') — what
  // the flat/commission bucket's rebate_fee row(s) key off, instead of a made-up sentinel.
  commissionFromTo?: number;
  // The commission row's own amount, per currency — what the flat bucket's Lump Sum target follows.
  commissionAmountsByCurrency?: Record<string, number>;
  // The Transaction Fee table's own currently-selected currency — this section's currency dropdown
  // follows it, since Lump Sum amounts (targets and shares) are only meaningful for that currency.
  transactionCurrencyId?: number;
  availableCurrencies: RebateFeeCurrency[];
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [parties, setParties] = useState<Party[]>([]);
  const [partiesLoading, setPartiesLoading] = useState(false);
  const [partiesError, setPartiesError] = useState<string | null>(null);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyAccountNumber, setNewPartyAccountNumber] = useState('');
  // Every rebate setting of this bank + commodity type shares one fee configuration, and the
  // party/fee/tier/share APIs resolve it from any one of the bank's settings — so this is simply the
  // first of them. '' (with noRebateSetting) until the bank has one.
  const [rebateSettingId, setRebateSettingId] = useState<number | string>('');
  const [noRebateSetting, setNoRebateSetting] = useState(false);
  const [useDealCount, setUseDealCount] = useState(false);
  const [flatBucket, setFlatBucket] = useState<FeeBucket>({ id: genId(), shares: [], tiers: [] });
  const [rangeBuckets, setRangeBuckets] = useState<FeeBucket[]>([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>('');
  // The fee type chosen for each currency of each range, keyed by rangeKey(fromTo, rangeType) then
  // currencyId — populated by reading whatever rebate_share rows already exist. A currency missing
  // from a range's map shows a blank ("Choose one") badge pill; nothing is created until the user
  // actually picks a value for that currency.
  const [typesByRangeKeyAndCurrency, setTypesByRangeKeyAndCurrency] = useState<Record<string, Record<string, FeeType>>>({});
  // Persisted share values, keyed by `${partyId}|${rangeKey(fromTo, rangeType)}|${currencyId}` —
  // every share always carries its own real currencyId now — loaded once per bank+commodityType.
  const [sharesByPartyFromToCurrency, setSharesByPartyFromToCurrency] = useState<Record<string, number>>({});
  // Which deal-count tiers actually exist (are saved in rebate_tier) for each range, keyed by
  // rangeKey(fromTo, rangeType) — a tier only ever shows up here once it's been added via the "Add
  // Deal Count Tier" button, which persists it immediately.
  const [tiersByRangeKey, setTiersByRangeKey] = useState<Record<string, TierDef[]>>({});
  // Same shape as typesByRangeKeyAndCurrency, but one level deeper for the tier: rangeKey -> tier
  // slot -> currencyId -> FeeType.
  const [tierTypesByRangeKeyAndSlot, setTierTypesByRangeKeyAndSlot] = useState<Record<string, Record<string, Record<string, FeeType>>>>(
    {}
  );
  // Persisted deal-count tier share values, keyed by `${rangeKey}|${tierSlot}|${partyId}|${currencyId}`.
  const [tierSharesByRangeSlotPartyCurrency, setTierSharesByRangeSlotPartyCurrency] = useState<Record<string, number>>({});
  // True once the fetch below has actually completed (even if it found nothing) — bucket hydration
  // waits for this too, so it doesn't run against an empty map before the real data has arrived.
  const [sharesLoaded, setSharesLoaded] = useState(false);

  const partiesRef = useRef(parties);
  useEffect(() => {
    partiesRef.current = parties;
  }, [parties]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<IRebateSetting[]>(`/api/rebate-settings/by-bank?bankId=${bankId}&commodityType=${commodityType}`)
      .then(res => {
        if (cancelled) {
          return;
        }
        const first = (res.data || [])[0];
        setRebateSettingId(first?.id ?? '');
        setNoRebateSetting(!first);
      })
      .catch(error => console.error('Error loading rebate settings:', error));
    return () => {
      cancelled = true;
    };
  }, [bankId, commodityType]);

  // Parties load independently of any rebate fee row, once the shared configuration is resolved.
  useEffect(() => {
    if (!rebateSettingId) {
      setParties([]);
      return;
    }
    let cancelled = false;
    setPartiesLoading(true);
    setPartiesError(null);
    axios
      .get<IRebateParty[]>(`/api/rebate-parties/by-setting/${rebateSettingId}`)
      .then(partiesRes => {
        if (cancelled) {
          return;
        }
        const loadedParties: Party[] = (partiesRes.data || []).map(p => ({ id: String(p.id), name: p.name ?? '', accountNumber: p.accountNumber ?? '' }));
        setParties(loadedParties);
        setFlatBucket(prev => reconcileBucketParties(prev, loadedParties));
        setRangeBuckets(prev => prev.map(b => reconcileBucketParties(b, loadedParties)));
      })
      .catch(error => {
        console.error('Error loading rebate parties:', error);
        if (!cancelled) {
          setPartiesError(translate('comexApp.bank.details.rebateFee.partiesLoadError'));
        }
      })
      .then(() => {
        if (!cancelled) {
          setPartiesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rebateSettingId]);

  // The flat/commission bucket and the fee ranges are never both "live" for the bank at the same
  // time, so "Use Deal Count" is tracked independently for each — switching it on while commission
  // is active must not also turn it on for fee ranges, and vice versa.
  const activeRangeType: 'commission' | 'range' = useCommission ? 'commission' : 'range';

  // Hydrate the "Use Deal Count" switch from whatever's already shared across this setting's
  // parties for the currently-active range type (defaults to false if nothing's been synced yet for
  // it). The flat bucket's fee type instead comes through the same read-and-hydrate pipeline ranges
  // use, below.
  useEffect(() => {
    if (!rebateSettingId) {
      return;
    }
    let cancelled = false;
    axios
      .get<IRebateFeeUseDealCount>(`/api/rebate-fees/use-deal-count?rebateSettingId=${rebateSettingId}&rangeType=${activeRangeType}`)
      .then(res => {
        if (cancelled) {
          return;
        }
        setUseDealCount(!!res.data.useDealCount);
      })
      .catch(error => {
        console.error('Error loading rebate fee useDealCount:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [rebateSettingId, activeRangeType]);

  // Follow the Transaction Fee table's own currency selection — Lump Sum amounts (targets and
  // shares) only make sense for whichever currency is actually configured there. Falls back to the
  // first available currency if the Transaction Fee table hasn't picked one yet (transactionCurrencyId
  // defaults to 0 before the bank's own currency loads).
  useEffect(() => {
    if (transactionCurrencyId) {
      setSelectedCurrencyId(String(transactionCurrencyId));
      return;
    }
    if (availableCurrencies?.length && !availableCurrencies.some(c => String(c.id) === selectedCurrencyId)) {
      setSelectedCurrencyId(String(availableCurrencies[0].id));
    }
  }, [transactionCurrencyId, availableCurrencies]);

  const currencyLabel = availableCurrencies?.find(c => String(c.id) === selectedCurrencyId)?.name ?? '';

  // Read whatever rebate_fee, rebate_tier, and rebate_share rows already exist for this setting —
  // purely a read, nothing is created here. A row only appears once the user has actually chosen
  // that range's fee type, or added a tier (see changeRangeBucketType / changeFlatBucketType /
  // addTierToBucket).
  useEffect(() => {
    if (!rebateSettingId) {
      return;
    }
    let cancelled = false;
    setSharesLoaded(false);
    Promise.all([
      axios.get<IRebateFee[]>(`/api/rebate-fees/by-setting?rebateSettingId=${rebateSettingId}`),
      axios.get<IRebateShare[]>(`/api/rebate-shares/by-setting?rebateSettingId=${rebateSettingId}`),
      axios.get<IRebateTier[]>(`/api/rebate-tiers/by-setting?rebateSettingId=${rebateSettingId}`),
    ])
      .then(([feesRes, sharesRes, tiersRes]) => {
        if (cancelled) {
          return;
        }
        const feeRows = feesRes.data || [];

        // Shares and tiers only carry rebateFeeId — resolve each back to (partyId, fromTo,
        // rangeType) via the rows above. rangeType is required here: a range's own upper bound can
        // numerically coincide with the commission row's own from_to, so fromTo alone can't tell
        // the two rows apart.
        const partyFromToByRebateFeeId: Record<number, { partyId: string; fromTo: number; rangeType: 'commission' | 'range' }> = {};
        feeRows.forEach(rf => {
          if (rf.id !== undefined && rf.rebatePartyId !== undefined && rf.fromTo !== undefined) {
            partyFromToByRebateFeeId[rf.id] = {
              partyId: String(rf.rebatePartyId),
              fromTo: rf.fromTo,
              rangeType: rf.rangeType === 'commission' ? 'commission' : 'range',
            };
          }
        });

        // Every party has its own separate rebate_tier row even for "the same" conceptual tier —
        // dedupe by (rangeKey, operator, dealCount) so each bucket sees one tier definition.
        const tierDefById: Record<number, TierDef> = {};
        (tiersRes.data || []).forEach(t => {
          if (t.id !== undefined && (t.operator === '<=' || t.operator === '>=') && t.dealCount !== undefined) {
            tierDefById[t.id] = { operator: t.operator, dealCount: t.dealCount };
          }
        });
        const tiersByRangeKeySets: Record<string, Record<string, TierDef>> = {};
        (tiersRes.data || []).forEach(t => {
          if (t.id === undefined || t.rebateFeeId === undefined) {
            return;
          }
          const loc = partyFromToByRebateFeeId[t.rebateFeeId];
          const def = tierDefById[t.id];
          if (!loc || !def) {
            return;
          }
          const rk = rangeKey(loc.fromTo, loc.rangeType);
          const slot = tierSlot(def.operator, def.dealCount);
          tiersByRangeKeySets[rk] = { ...(tiersByRangeKeySets[rk] || {}), [slot]: def };
        });
        const tiersByRangeKeyResult: Record<string, TierDef[]> = {};
        Object.entries(tiersByRangeKeySets).forEach(([rk, bySlot]) => {
          tiersByRangeKeyResult[rk] = Object.values(bySlot);
        });

        // Every share always carries its own real currencyId now — fee type is fully independent per
        // currency (and per tier), with no shared/currency-agnostic row of any kind.
        const typesMap: Record<string, Record<string, FeeType>> = {};
        const sharesMap: Record<string, number> = {};
        const tierTypesMap: Record<string, Record<string, Record<string, FeeType>>> = {};
        const tierSharesMap: Record<string, number> = {};
        (sharesRes.data || []).forEach(share => {
          if (share.rebateFeeId === undefined || share.currencyId === undefined || share.currencyId === null) {
            return;
          }
          const loc = partyFromToByRebateFeeId[share.rebateFeeId];
          if (!loc) {
            return;
          }
          const rk = rangeKey(loc.fromTo, loc.rangeType);
          const currencyKey = String(share.currencyId);

          if (share.rebateTierId !== undefined && share.rebateTierId !== null) {
            const def = tierDefById[share.rebateTierId];
            if (!def) {
              return;
            }
            const slot = tierSlot(def.operator, def.dealCount);
            if (share.feeType === 'Lump Sum' || share.feeType === 'Percentage') {
              tierTypesMap[rk] = {
                ...(tierTypesMap[rk] || {}),
                [slot]: { ...((tierTypesMap[rk] || {})[slot] || {}), [currencyKey]: share.feeType },
              };
            }
            tierSharesMap[`${rk}|${slot}|${loc.partyId}|${currencyKey}`] = Number(share.value) || 0;
            return;
          }

          if (share.feeType === 'Lump Sum' || share.feeType === 'Percentage') {
            typesMap[rk] = { ...(typesMap[rk] || {}), [currencyKey]: share.feeType };
          }
          sharesMap[`${loc.partyId}|${rk}|${currencyKey}`] = Number(share.value) || 0;
        });

        setTiersByRangeKey(tiersByRangeKeyResult);
        setTierTypesByRangeKeyAndSlot(tierTypesMap);
        setTierSharesByRangeSlotPartyCurrency(tierSharesMap);
        setTypesByRangeKeyAndCurrency(typesMap);
        setSharesByPartyFromToCurrency(sharesMap);
        setSharesLoaded(true);
      })
      .catch(error => {
        console.error('Error loading rebate fees/shares/tiers:', error);
        if (!cancelled) {
          setSharesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rebateSettingId]);

  // Apply whatever share values have already been persisted for this bucket's parties — every
  // currency at once, so switching the currency dropdown later shows the right value with no refetch.
  const hydrateShares = (shares: ShareRow[], rangeKeyStr: string): ShareRow[] => {
    const prefix = `${rangeKeyStr}|`;
    return shares.map(s => {
      const valuesByCurrency = { ...s.valuesByCurrency };
      const partyPrefix = `${s.partyId}|${prefix}`;
      Object.entries(sharesByPartyFromToCurrency).forEach(([mapKey, value]) => {
        if (mapKey.startsWith(partyPrefix)) {
          valuesByCurrency[mapKey.slice(partyPrefix.length)] = value;
        }
      });
      return { ...s, valuesByCurrency };
    });
  };

  const hydrateTierShares = (shares: ShareRow[], rangeKeyStr: string, slot: string): ShareRow[] => {
    const prefix = `${rangeKeyStr}|${slot}|`;
    return shares.map(s => {
      const valuesByCurrency = { ...s.valuesByCurrency };
      const partyPrefix = `${prefix}${s.partyId}|`;
      Object.entries(tierSharesByRangeSlotPartyCurrency).forEach(([mapKey, value]) => {
        if (mapKey.startsWith(partyPrefix)) {
          valuesByCurrency[mapKey.slice(partyPrefix.length)] = value;
        }
      });
      return { ...s, valuesByCurrency };
    });
  };

  // Rebuild a bucket's tier list from whatever's actually been saved in rebate_tier for this range —
  // a tier only appears once it's been added, matching "no set tier if it's not saved yet".
  const buildTiers = (existingTiers: BucketTier[], rangeKeyStr: string | undefined): BucketTier[] => {
    const defs = (rangeKeyStr ? tiersByRangeKey[rangeKeyStr] : undefined) || [];
    return defs.map(def => {
      const slot = tierSlot(def.operator, def.dealCount);
      const existing = existingTiers.find(t => t.operator === def.operator && t.dealCount === def.dealCount);
      const baseShares = existing ? existing.shares : makeShares(parties);
      const canHydrateShares = !(existing && existing.sharesHydrated) && baseShares.length > 0 && sharesLoaded;
      return {
        operator: def.operator,
        dealCount: def.dealCount,
        typesByCurrency: rangeKeyStr ? tierTypesByRangeKeyAndSlot[rangeKeyStr]?.[slot] : undefined,
        shares: canHydrateShares && rangeKeyStr ? hydrateTierShares(baseShares, rangeKeyStr, slot) : baseShares,
        sharesHydrated: (existing && existing.sharesHydrated) || canHydrateShares,
      };
    });
  };

  // Hydrate the flat/commission bucket's fee type (per currency), tiers, and shares the same way
  // ranges do. typesByCurrency/tiers are pure derived read data, re-applied every time regardless —
  // only share VALUES need a "hydrated once" guard, since those can also come from the user's own
  // edits in between loads. That guard only flips once parties have loaded (a share row exists per
  // party to fill in) AND the shares fetch has actually completed — running before either is ready
  // would wrongly mark itself done against nothing, and never retry once the missing piece arrives.
  useEffect(() => {
    if (!useCommission || commissionFromTo === undefined) {
      return;
    }
    const key = rangeKey(commissionFromTo, 'commission');
    setFlatBucket(prev => {
      const canHydrateShares = !prev.sharesHydrated && prev.shares.length > 0 && sharesLoaded;
      return {
        ...prev,
        to: commissionFromTo,
        amountsByCurrency: commissionAmountsByCurrency,
        typesByCurrency: typesByRangeKeyAndCurrency[key],
        tiers: buildTiers(prev.tiers, key),
        shares: canHydrateShares ? hydrateShares(prev.shares, key) : prev.shares,
        sharesHydrated: prev.sharesHydrated || canHydrateShares,
      };
    });
  }, [
    useCommission,
    commissionFromTo,
    commissionAmountsByCurrency,
    typesByRangeKeyAndCurrency,
    tiersByRangeKey,
    tierTypesByRangeKeyAndSlot,
    sharesByPartyFromToCurrency,
    tierSharesByRangeSlotPartyCurrency,
    sharesLoaded,
    parties,
  ]);

  // Ranges themselves always mirror the Transaction Fee table above (read-only here);
  // only the rebate split (type/shares/tiers) is owned by this section. typesByCurrency/tiers are
  // pure derived read data, re-applied every time; only share VALUES need the "hydrated once" guard
  // (see the flat bucket effect above for why).
  useEffect(() => {
    setRangeBuckets(prev =>
      (feeRanges || []).map(range => {
        const transactionFeeType: FeeType = range.feeType === 'Percentage' ? 'Percentage' : 'Lump Sum';
        const key = range.to !== undefined ? rangeKey(range.to, 'range') : undefined;
        const existing = prev.find(b => b.sourceId === range.id);
        if (existing) {
          const canHydrateShares = !existing.sharesHydrated && existing.shares.length > 0 && sharesLoaded;
          return {
            ...existing,
            from: range.from,
            to: range.to,
            transactionFeeType,
            amountsByCurrency: range.amountsByCurrency,
            typesByCurrency: key ? typesByRangeKeyAndCurrency[key] : undefined,
            tiers: buildTiers(existing.tiers, key),
            shares: canHydrateShares && key ? hydrateShares(existing.shares, key) : existing.shares,
            sharesHydrated: existing.sharesHydrated || canHydrateShares,
          };
        }
        const newShares = makeShares(parties);
        const canHydrateShares = newShares.length > 0 && sharesLoaded;
        return {
          id: genId(),
          sourceId: range.id,
          from: range.from,
          to: range.to,
          transactionFeeType,
          amountsByCurrency: range.amountsByCurrency,
          typesByCurrency: key ? typesByRangeKeyAndCurrency[key] : undefined,
          tiers: buildTiers([], key),
          shares: canHydrateShares && key ? hydrateShares(newShares, key) : newShares,
          sharesHydrated: canHydrateShares,
        };
      })
    );
  }, [
    feeRanges,
    typesByRangeKeyAndCurrency,
    tiersByRangeKey,
    tierTypesByRangeKeyAndSlot,
    sharesByPartyFromToCurrency,
    tierSharesByRangeSlotPartyCurrency,
    sharesLoaded,
    parties,
  ]);

  // After a type change succeeds, record it for just this currency — every other currency's type is
  // untouched.
  const applyLocalFeeTypeChange = (key: string, newType: FeeType) => {
    setTypesByRangeKeyAndCurrency(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [selectedCurrencyId]: newType } }));
  };

  const changeRangeBucketType = (bucket: FeeBucket, newType: FeeType) => {
    if (bucket.to === undefined) {
      return;
    }
    const fromTo = bucket.to;
    axios
      .put('/api/rebate-shares/fee-type', {
        rebateSettingId,
        commodityType,
        fromTo,
        feeType: newType,
        rangeType: 'range',
        currencyId: Number(selectedCurrencyId),
      })
      .then(() => applyLocalFeeTypeChange(rangeKey(fromTo, 'range'), newType))
      .catch(error => {
        console.error('Error updating rebate fee type:', error);
      });
  };

  const changeFlatBucketType = (newType: FeeType) => {
    if (!rebateSettingId || commissionFromTo === undefined) {
      return;
    }
    axios
      .put('/api/rebate-shares/fee-type', {
        rebateSettingId,
        commodityType,
        fromTo: commissionFromTo,
        feeType: newType,
        rangeType: 'commission',
        currencyId: Number(selectedCurrencyId),
      })
      .then(() => applyLocalFeeTypeChange(rangeKey(commissionFromTo, 'commission'), newType))
      .catch(error => {
        console.error('Error updating rebate fee type:', error);
      });
  };

  // Persists one party's share value for a bucket's range + currently-selected currency. Every share
  // — Lump Sum or Percentage — is scoped to that real currency, independent of every other currency's
  // type and value. rangeType disambiguates a range whose from_to happens to numerically coincide
  // with the commission row's own from_to.
  const saveShare = (bucket: FeeBucket, rangeType: 'commission' | 'range', partyId: string, value: number) => {
    if (bucket.to === undefined) {
      return;
    }
    axios
      .put('/api/rebate-shares', {
        rebatePartyId: Number(partyId),
        commodityType,
        fromTo: bucket.to,
        rangeType,
        currencyId: Number(selectedCurrencyId),
        value,
      })
      .catch(error => {
        console.error('Error saving rebate share:', error);
      });
  };

  // Adds a deal-count tier to one bucket, persisting it immediately — it only shows up in the table
  // once this succeeds (see buildTiers / "No deal-count tiers yet.").
  const addTierToBucket = (bucket: FeeBucket, rangeType: 'commission' | 'range') => {
    if (!rebateSettingId || bucket.to === undefined) {
      return;
    }
    const fromTo = bucket.to;
    const def: TierDef = { operator: '<=', dealCount: 0 };
    axios
      .post('/api/rebate-tiers', { rebateSettingId, commodityType, fromTo, rangeType, operator: def.operator, dealCount: def.dealCount })
      .then(() => {
        const key = rangeKey(fromTo, rangeType);
        setTiersByRangeKey(prev => ({ ...prev, [key]: [...(prev[key] || []), def] }));
      })
      .catch(error => {
        console.error('Error adding deal-count tier:', error);
      });
  };

  // Renames a tier's boundary in place across every party, then re-keys whatever type/share data was
  // already loaded for it locally, so in-progress values aren't lost until the next full reload.
  const updateTierDefInBucket = (bucket: FeeBucket, rangeType: 'commission' | 'range', oldDef: TierDef, newDef: TierDef) => {
    if (bucket.to === undefined) {
      return;
    }
    const fromTo = bucket.to;
    axios
      .put('/api/rebate-tiers', {
        rebateSettingId,
        commodityType,
        fromTo,
        rangeType,
        oldOperator: oldDef.operator,
        oldDealCount: oldDef.dealCount,
        newOperator: newDef.operator,
        newDealCount: newDef.dealCount,
      })
      .then(() => {
        const key = rangeKey(fromTo, rangeType);
        const oldSlot = tierSlot(oldDef.operator, oldDef.dealCount);
        const newSlot = tierSlot(newDef.operator, newDef.dealCount);
        setTiersByRangeKey(prev => ({
          ...prev,
          [key]: (prev[key] || []).map(d => (d.operator === oldDef.operator && d.dealCount === oldDef.dealCount ? newDef : d)),
        }));
        if (oldSlot !== newSlot) {
          setTierTypesByRangeKeyAndSlot(prev => {
            const forRange = prev[key];
            if (!forRange || !forRange[oldSlot]) {
              return prev;
            }
            const rest = { ...forRange };
            const moved = rest[oldSlot];
            delete rest[oldSlot];
            return { ...prev, [key]: { ...rest, [newSlot]: moved } };
          });
          setTierSharesByRangeSlotPartyCurrency(prev => {
            const oldPrefix = `${key}|${oldSlot}|`;
            const newPrefix = `${key}|${newSlot}|`;
            const next = { ...prev };
            Object.keys(prev).forEach(k => {
              if (k.startsWith(oldPrefix)) {
                next[newPrefix + k.slice(oldPrefix.length)] = next[k];
                delete next[k];
              }
            });
            return next;
          });
        }
      })
      .catch(error => {
        console.error('Error updating deal-count tier:', error);
      });
  };

  const deleteTierFromBucket = (bucket: FeeBucket, rangeType: 'commission' | 'range', def: TierDef) => {
    if (bucket.to === undefined) {
      return;
    }
    const fromTo = bucket.to;
    axios
      .delete('/api/rebate-tiers', {
        data: { rebateSettingId, commodityType, fromTo, rangeType, operator: def.operator, dealCount: def.dealCount },
      })
      .then(() => {
        const key = rangeKey(fromTo, rangeType);
        setTiersByRangeKey(prev => ({
          ...prev,
          [key]: (prev[key] || []).filter(d => !(d.operator === def.operator && d.dealCount === def.dealCount)),
        }));
      })
      .catch(error => {
        console.error('Error deleting deal-count tier:', error);
      });
  };

  const changeTierType = (bucket: FeeBucket, rangeType: 'commission' | 'range', tier: BucketTier, newType: FeeType) => {
    if (bucket.to === undefined) {
      return;
    }
    const fromTo = bucket.to;
    axios
      .put('/api/rebate-shares/fee-type', {
        rebateSettingId,
        commodityType,
        fromTo,
        feeType: newType,
        rangeType,
        tierOperator: tier.operator,
        tierDealCount: tier.dealCount,
        currencyId: Number(selectedCurrencyId),
      })
      .then(() => {
        const key = rangeKey(fromTo, rangeType);
        const slot = tierSlot(tier.operator, tier.dealCount);
        setTierTypesByRangeKeyAndSlot(prev => ({
          ...prev,
          [key]: { ...(prev[key] || {}), [slot]: { ...((prev[key] || {})[slot] || {}), [selectedCurrencyId]: newType } },
        }));
      })
      .catch(error => {
        console.error('Error updating deal-count tier fee type:', error);
      });
  };

  const saveTierShare = (bucket: FeeBucket, rangeType: 'commission' | 'range', tier: BucketTier, partyId: string, value: number) => {
    if (bucket.to === undefined) {
      return;
    }
    axios
      .put('/api/rebate-shares', {
        rebatePartyId: Number(partyId),
        commodityType,
        fromTo: bucket.to,
        rangeType,
        tierOperator: tier.operator,
        tierDealCount: tier.dealCount,
        currencyId: Number(selectedCurrencyId),
        value,
      })
      .catch(error => {
        console.error('Error saving deal-count tier share:', error);
      });
  };

  const handleUseDealCountChange = (checked: boolean) => {
    if (!rebateSettingId) {
      return;
    }
    const previous = useDealCount;
    setUseDealCount(checked);
    axios
      .put('/api/rebate-fees/use-deal-count', { rebateSettingId, rangeType: activeRangeType, useDealCount: checked })
      .catch(error => {
        console.error('Error updating rebate fee useDealCount:', error);
        setUseDealCount(previous);
      });
  };

  // A third party is always added together with its account number, never name first.
  const canAddParty = !!rebateSettingId && !!newPartyName.trim() && !!newPartyAccountNumber.trim();

  const addParty = () => {
    const name = newPartyName.trim();
    const accountNumber = newPartyAccountNumber.trim();
    if (!canAddParty) {
      return;
    }
    axios
      .post<IRebateParty>('/api/rebate-parties', { bankId, commodityType, name, accountNumber })
      .then(res => {
        const party: Party = { id: String(res.data.id), name: res.data.name ?? name, accountNumber: res.data.accountNumber ?? accountNumber };
        const appendShare = (shares: ShareRow[]) => [...shares, { partyId: party.id, active: true, valuesByCurrency: {} }];
        setParties(prev => [...prev, party]);
        setFlatBucket(prev => ({
          ...prev,
          shares: appendShare(prev.shares),
          tiers: prev.tiers.map(t => ({ ...t, shares: appendShare(t.shares) })),
        }));
        setRangeBuckets(prev =>
          prev.map(b => ({
            ...b,
            shares: appendShare(b.shares),
            tiers: b.tiers.map(t => ({ ...t, shares: appendShare(t.shares) })),
          }))
        );
        setNewPartyName('');
        setNewPartyAccountNumber('');
        setPartiesError(null);
      })
      .catch(error => {
        console.error('Error creating rebate party:', error);
        setPartiesError(translate('comexApp.bank.details.rebateFee.partyAddError'));
      });
  };

  const saveAccountNumber = (partyId: string, accountNumber: string) => {
    setParties(prev => prev.map(p => (p.id === partyId ? { ...p, accountNumber } : p)));
    axios.patch(`/api/rebate-parties/${partyId}`, { id: Number(partyId), accountNumber }).catch(error => {
      console.error('Error saving rebate party account number:', error);
      setPartiesError(translate('comexApp.bank.details.rebateFee.partyAccountNumberSaveError'));
    });
  };

  const removeParty = (partyId: string) => {
    axios
      .delete(`/api/rebate-parties/${partyId}`)
      .then(() => {
        setPartiesError(null);
        const dropShare = (shares: ShareRow[]) => shares.filter(s => s.partyId !== partyId);
        setParties(prev => prev.filter(p => p.id !== partyId));
        setFlatBucket(prev => ({
          ...prev,
          shares: dropShare(prev.shares),
          tiers: prev.tiers.map(t => ({ ...t, shares: dropShare(t.shares) })),
        }));
        setRangeBuckets(prev =>
          prev.map(b => ({
            ...b,
            shares: dropShare(b.shares),
            tiers: b.tiers.map(t => ({ ...t, shares: dropShare(t.shares) })),
          }))
        );
      })
      .catch(error => {
        console.error('Error deleting rebate party:', error);
        setPartiesError(translate('comexApp.bank.details.rebateFee.partyRemoveError'));
      });
  };

  return (
    <div className="collapse-section">
      <Grid container className="align-items-center">
        <Grid item xs={10} sm={11} md={11.5}>
          <Typography variant="h5" component="div" data-cy="rebateFeeHeading">
            <Translate contentKey="comexApp.bank.details.rebateFee.heading" />
          </Typography>
        </Grid>
        <Grid item xs={2} sm={1} md={0.5}>
          <IconButton aria-label="collapse" onClick={() => setCollapsed(prev => !prev)}>
            {!collapsed ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={!collapsed}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          <Translate
            contentKey={
              commodityType === 'Local'
                ? 'comexApp.bank.details.rebateFee.sharedHintLocal'
                : 'comexApp.bank.details.rebateFee.sharedHintInternational'
            }
          />
        </Typography>
        {noRebateSetting && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            <Translate contentKey="comexApp.bank.details.rebateFee.noRebateSetting" />
          </Typography>
        )}
        <br />

        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <FormControl sx={{ width: { md: 300, xs: 280 }, display: 'flex', justifyContent: 'flex-start' }}>
            <Input type="select" width={'80%'} value={selectedCurrencyId} onChange={e => setSelectedCurrencyId(e.target.value)}>
              {(availableCurrencies || []).map(c => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </Input>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 3 }}>
          <Translate contentKey="comexApp.bank.details.rebateFee.currencyHint" />
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          <Translate contentKey="comexApp.bank.details.rebateFee.parties" />
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          {parties.map(party => (
            <Box key={party.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={partyNameColumnSx}>
                <Chip
                  label={party.name}
                  onDelete={() => removeParty(party.id)}
                  sx={isRtlLocale() ? { '& .MuiChip-deleteIcon': { marginLeft: '5px', marginRight: '-6px' } } : undefined}
                />
              </Box>
              {isDefaultParty(party, bankName) ? (
                <Typography variant="caption" color="text.secondary">
                  <Translate contentKey="comexApp.bank.details.rebateFee.defaultPartyAccountHint" />
                </Typography>
              ) : (
                <TextField
                  size="small"
                  placeholder={translate('comexApp.bank.details.rebateFee.partyAccountNumberPlaceholder')}
                  defaultValue={party.accountNumber ?? ''}
                  onBlur={e => {
                    const value = e.target.value.trim();
                    if (!value) {
                      // A third party's account number is required — put the saved one back.
                      e.target.value = party.accountNumber ?? '';
                    } else if (value !== (party.accountNumber ?? '')) {
                      saveAccountNumber(party.id, value);
                    }
                  }}
                  sx={compactFieldSx}
                />
              )}
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={translate('comexApp.bank.details.rebateFee.addPartyPlaceholder')}
              value={newPartyName}
              onChange={e => setNewPartyName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  addParty();
                }
              }}
              sx={{ ...compactFieldSx, ...partyNameColumnSx }}
            />
            <TextField
              size="small"
              placeholder={translate('comexApp.bank.details.rebateFee.partyAccountNumberPlaceholder')}
              value={newPartyAccountNumber}
              onChange={e => setNewPartyAccountNumber(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  addParty();
                }
              }}
              sx={compactFieldSx}
            />
            <IconButton color="primary" onClick={addParty} disabled={!canAddParty}>
              <AddCircleIcon />
            </IconButton>
          </Box>
        </Box>
        {partiesLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <Translate contentKey="comexApp.bank.details.rebateFee.loadingParties" />
          </Typography>
        )}
        {partiesError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {partiesError}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          <Translate contentKey="comexApp.bank.details.rebateFee.partiesHint" />
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          <Translate contentKey="comexApp.bank.details.rebateFee.splitBasisLabel" />
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 3 }}>
          <BasisOptionCard
            selected={!useDealCount}
            disabled={!rebateSettingId}
            title={<Translate contentKey="comexApp.bank.details.rebateFee.dealAmountOption" />}
            onSelect={() => handleUseDealCountChange(false)}
          />
          <BasisOptionCard
            selected={useDealCount}
            disabled={!rebateSettingId}
            title={<Translate contentKey="comexApp.bank.details.rebateFee.dealCountOption" />}
            onSelect={() => handleUseDealCountChange(true)}
          />
        </Box>

        {useCommission ? (
          <FeeBucketCard
            bucket={flatBucket}
            parties={parties}
            useDealCount={useDealCount}
            isRangeMode={false}
            currencyId={selectedCurrencyId}
            currencyLabel={currencyLabel}
            onUpdate={fn => setFlatBucket(fn)}
            onChangeType={changeFlatBucketType}
            onSaveShare={(partyId, value) => saveShare(flatBucket, 'commission', partyId, value)}
            onAddTier={() => addTierToBucket(flatBucket, 'commission')}
            onUpdateTierDef={(oldDef, newDef) => updateTierDefInBucket(flatBucket, 'commission', oldDef, newDef)}
            onDeleteTierDef={def => deleteTierFromBucket(flatBucket, 'commission', def)}
            onChangeTierType={(tier, newType) => changeTierType(flatBucket, 'commission', tier, newType)}
            onSaveTierShare={(tier, partyId, value) => saveTierShare(flatBucket, 'commission', tier, partyId, value)}
          />
        ) : (
          <>
            {rangeBuckets.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Translate contentKey="comexApp.bank.details.rebateFee.noRangesConfigured" />
              </Typography>
            )}
            {rangeBuckets.map(bucket => (
              <FeeBucketCard
                key={bucket.id}
                bucket={bucket}
                parties={parties}
                useDealCount={useDealCount}
                isRangeMode
                currencyId={selectedCurrencyId}
                currencyLabel={currencyLabel}
                onUpdate={fn => setRangeBuckets(prev => prev.map(b => (b.id === bucket.id ? fn(b) : b)))}
                onChangeType={newType => changeRangeBucketType(bucket, newType)}
                onSaveShare={(partyId, value) => saveShare(bucket, 'range', partyId, value)}
                onAddTier={() => addTierToBucket(bucket, 'range')}
                onUpdateTierDef={(oldDef, newDef) => updateTierDefInBucket(bucket, 'range', oldDef, newDef)}
                onDeleteTierDef={def => deleteTierFromBucket(bucket, 'range', def)}
                onChangeTierType={(tier, newType) => changeTierType(bucket, 'range', tier, newType)}
                onSaveTierShare={(tier, partyId, value) => saveTierShare(bucket, 'range', tier, partyId, value)}
              />
            ))}
          </>
        )}
      </Collapse>
    </div>
  );
}
